import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Modal, Form, Input, Select, Space, Tag, Popconfirm, Upload, message, Typography } from 'antd';
import { PlusOutlined, UploadOutlined, DeleteOutlined, EditOutlined, SearchOutlined } from '@ant-design/icons';
import { embotAPI } from '../../services/api';

const { Text } = Typography;

export default function EMContacts() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState('');
  const [form] = Form.useForm();

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await embotAPI.listContacts({ search: search || undefined, limit: 500 });
      setContacts(res.data || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, [search]);

  useEffect(() => { fetch(); }, [fetch]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (editingId) {
        await embotAPI.updateContact(editingId, values);
        message.success('Contact updated');
      } else {
        await embotAPI.createContact(values);
        message.success('Contact created');
      }
      setModalOpen(false);
      form.resetFields();
      setEditingId(null);
      fetch();
    } catch (e) {
      if (e.message) message.error(e.message);
    }
  };

  const handleEdit = (record) => {
    setEditingId(record.id);
    form.setFieldsValue(record);
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    await embotAPI.deleteContact(id);
    message.success('Deleted');
    fetch();
  };

  const handleImport = async (info) => {
    try {
      const res = await embotAPI.importContacts(info.file);
      message.success(`Imported: ${res.data.created} created, ${res.data.skipped} skipped`);
      fetch();
    } catch (e) {
      message.error(e.message);
    }
  };

  const columns = [
    { title: 'Name', dataIndex: 'name', width: 140, ellipsis: true },
    { title: 'Email', dataIndex: 'email', width: 200, ellipsis: true, render: v => <Text copyable={{ text: v }}>{v}</Text> },
    { title: 'Company', dataIndex: 'company', width: 140, ellipsis: true },
    { title: 'Title', dataIndex: 'title', width: 120, ellipsis: true },
    {
      title: 'Tags', dataIndex: 'tags', width: 180,
      render: tags => (tags || []).map(t => <Tag key={t} color="blue">{t}</Tag>),
    },
    {
      title: 'Metrics', dataIndex: 'metrics', width: 140,
      render: m => m ? <Text type="secondary" style={{ fontSize: 12 }}>S:{m.sent || 0} O:{m.opened || 0} R:{m.replied || 0}</Text> : '-',
    },
    {
      title: 'Actions', width: 100, fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Popconfirm title="Delete?" onConfirm={() => handleDelete(record.id)}>
            <Button size="small" icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search contacts..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ width: 260 }}
          allowClear
        />
        <Space>
          <Upload accept=".csv" showUploadList={false} customRequest={handleImport}>
            <Button icon={<UploadOutlined />}>Import CSV</Button>
          </Upload>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditingId(null); form.resetFields(); setModalOpen(true); }}>
            Add Contact
          </Button>
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={contacts}
        rowKey="id"
        loading={loading}
        size="middle"
        pagination={{ pageSize: 20 }}
        scroll={{ x: 1000 }}
      />

      <Modal
        title={editingId ? 'Edit Contact' : 'New Contact'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => { setModalOpen(false); setEditingId(null); form.resetFields(); }}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Name" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}><Input /></Form.Item>
          <Form.Item name="company" label="Company"><Input /></Form.Item>
          <Form.Item name="title" label="Title"><Input /></Form.Item>
          <Form.Item name="phone" label="Phone"><Input /></Form.Item>
          <Form.Item name="tags" label="Tags">
            <Select mode="tags" placeholder="Type and press Enter" />
          </Form.Item>
          <Form.Item name="notes" label="Notes"><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
